import React, { FunctionComponent, MouseEventHandler, useEffect } from "react";
import moment from "moment";
import { IconButton, LinearProgress as MuiLinearProgress, ListItem, ListItemText } from "@mui/material";
import { Close } from "@mui/icons-material";
import { CheckboxMarkedCircleOutline } from "mdi-material-ui";
import { Alert, LinearProgress, Link, ThemeProvider, Typography } from "@hxnova/react-components";
import { NovaTheme } from "@hxnova/themes";
import { Icon } from "@hxnova/icons";
import useSwitch from "@react-hook/switch";

import "../../css/running_process_overlay_item.css";
import ProcessStatus from "../models/domain/enums/process_status";
import triggerFileDownload from "../services/utilities/general/trigger_file_download";
import { useFlags } from "../services/utilities/launch_darkly_helper";
import { IFC_TYPE_NAMES } from "./dialogs/batch_ifc_floor_selection_dialog";

export type Props = {
  title: string
  startTime: Date,
  endTime?: Date,
  status?: ProcessStatus,
  onDismiss: MouseEventHandler,
  downloadUrl?: string,
  downloadFileName?: string,
  onRetry?: () => void,
  onContactSupport?: () => void,
}

function toIfcMessage(running: boolean, succeeded: boolean, downloadFileName: string | null) {
  const ifcType = downloadFileName == null ? null :
                  (downloadFileName.includes("deviation") ? "deviations" :
                   downloadFileName.includes("as_built") ? "as_built" : "not_built");
  if (running) {
    return "Generating your IFC export… Your file will save to your Downloads folder automatically.";
  } else if (succeeded) {
    if (ifcType == null) {
      return "IFC export has been downloaded automatically to your Downloads.";
    }

    const ifcTypeName = ifcType === "not_built"
        || ifcType === "as_built"
        || ifcType === "deviations" ? IFC_TYPE_NAMES[ifcType] : ifcType;
    return `IFC for ${ifcTypeName} has been downloaded automatically to your Downloads.`;
  } else {
    return "Couldn't generate your IFC export. Something went wrong, please try again.";
  }
}

const RunningProcessOverlayItem: FunctionComponent<Props> = (props) => {
  let [dismiss, toggleDismiss] = useSwitch(false);
  const { ifcDownloadImprovements: ifcDownloadImprovementsFlag } = useFlags();
  const startedText = `started ${moment(props.startTime).fromNow()}`;
  const handleClose: MouseEventHandler = (event) => {
    props.onDismiss?.(event);
    toggleDismiss.on();
  };

  useEffect(() => {
    const tDelta = new Date().getMilliseconds() - new Date(props.endTime).getMilliseconds();
    // Don't auto-dismiss while there's a View file / Retry action to click.
    const isDownloadableSuccess = props.status === ProcessStatus.SUCCEEDED && !!props.downloadUrl;
    const isRetryableFailure = props.status !== ProcessStatus.RUNNING && props.status !== ProcessStatus.SUCCEEDED && !!props.onRetry;

    const isFailureWithSupport = props.status !== ProcessStatus.RUNNING && props.status !== ProcessStatus.SUCCEEDED && !!props.onContactSupport;
    let timeout = (props.status === ProcessStatus.RUNNING || isDownloadableSuccess || isRetryableFailure || isFailureWithSupport) ? null : 10000 - tDelta;
    let t;
    if (timeout && timeout > 0) {
      t = setTimeout(toggleDismiss.on, timeout);
    }

    return () => clearTimeout(t);
  }, [toggleDismiss, props.status, props.endTime, props.downloadUrl, props.onRetry, props.onContactSupport]); //ts lint fix. actually wont be of consequence.

  const succeeded = props.status === ProcessStatus.SUCCEEDED;
  const running = props.status === ProcessStatus.RUNNING;

  // Legacy MUI toast, preserved verbatim while the ifcDownloadImprovements flag is
  // off so the existing pipeline/export toasts are untouched until release.
  if (!ifcDownloadImprovementsFlag) {
    const legacyRunning = <>
      <div className="text-and-close-button">
        <div>
          <IconButton onClick={props.onDismiss}><Close/></IconButton>
        </div>
        <ListItemText
          primary={`${props.title}`}
          secondary={startedText}
        />
      </div>
      <MuiLinearProgress
        className="RunningProcessOverlayItem-progressBar"
        variant="indeterminate"
      />
    </>;
    const legacyCompleted = <ListItemText
      primary={<><CheckboxMarkedCircleOutline htmlColor={"var(--success-green)"}/>{props.title}</>}
      secondary={startedText}
    />;
    const legacyTerminated = <ListItemText
      primary={<><Close htmlColor={"var(--deviation-error-red)"}/>{props.title}</>}
      secondary={<>
        {startedText}
        <br/>
        {`${moment(props.startTime).format("ddd, MMM Do YYYY, h:mm:ss A")} - ${moment(props.endTime).format("h:mm:ss A")}`}
      </>}
    />;
    return <ListItem
      style={{ display: dismiss ? "none" : "inherit" }}
      className="RunningProcessOverlayItem"
    >
      {succeeded ? legacyCompleted : running ? legacyRunning : legacyTerminated}
    </ListItem>;
  }

  const color = succeeded ? "success" : running ? "info" : "error";
  // The deviation/as-built/not-built exports all run the `generate-batch-ifc`
  // pipeline; show the designed IFC-export copy for those, and the generic
  // pipeline-name toast for everything else (BCF, reports, etc.).
  const isIfcExport = /batch ifc/i.test(props.title);
  const showContactSupport = !running && !succeeded && !!props.onContactSupport;

  const iconName = running
    ? (isIfcExport ? "downloading" : "sync")
    : succeeded ? "check_circle" : "error";
  // Nova's Icon defaults to size "inherit" (~16px from the ambient body font); pin it to
  // 20px so the leading icon matches the toast's message text scale.
  const startDecorator = <Icon family="material" name={iconName} filled={succeeded} size={20} />;

  // onClick is a user gesture, so the cross-origin download / re-trigger aren't popup-blocked.
  const ifcAction = !isIfcExport ? undefined
    : (succeeded && props.downloadUrl)
      ? { label: "Download Again", onClick: () => { triggerFileDownload(props.downloadUrl!, props.downloadFileName || "ifc-export.zip"); } }
      : (!running && !succeeded && props.onRetry)
        ? { label: "Retry", onClick: props.onRetry }
        : undefined;

  return <ThemeProvider theme={NovaTheme} defaultMode="light" storageManager={null}>
    <div style={{ display: dismiss ? "none" : "block" }} className="RunningProcessOverlayItem RunningProcessOverlayItem--nova">
      <Alert
        className="RunningProcessOverlayItem-alert"
        color={color}
        intensity="subtle"
        startDecorator={startDecorator}
        action={ifcAction}
        onClose={(running || !!ifcAction || showContactSupport) ? ((event) => handleClose(event as any)) : undefined}
      >
        {isIfcExport ? (
          <div className="RunningProcessOverlayItem-body">
            <Typography variant="bodyMedium" className="RunningProcessOverlayItem-message">{toIfcMessage(running, succeeded, props.downloadFileName)}</Typography>
          </div>
        ) : (
          <div className="RunningProcessOverlayItem-body">
            <Typography variant="bodyMedium" className="RunningProcessOverlayItem-title">{props.title}</Typography>
            <Typography variant="bodySmall" className="RunningProcessOverlayItem-meta">
              {startedText}
              {!running && !succeeded && <>
                <br/>
                {`${moment(props.startTime).format("ddd, MMM Do YYYY, h:mm:ss A")} - ${moment(props.endTime).format("h:mm:ss A")}`}
              </>}
            </Typography>
            {running && <LinearProgress className="RunningProcessOverlayItem-progressBar" />}
          </div>
        )}
        {showContactSupport && (
          <Link
            component="button"
            variant="bodySmall"
            className="RunningProcessOverlayItem-supportLink"
            onClick={props.onContactSupport}
          >
            Contact Support Center
          </Link>
        )}
      </Alert>
    </div>
  </ThemeProvider>;
};

export default React.memo(RunningProcessOverlayItem);
